import { cva, type VariantProps } from 'class-variance-authority'
import { Info } from 'lucide-react'
import type * as React from 'react'
import { cn } from '../lib/cn'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Tooltip } from './tooltip'

/**
 * Chip tones (#1232, guard G6). A tone is a claim about the row, so pick it by meaning, never by looks.
 * The same table is in docs/ux-chip-tones.md.
 *
 * | tone         | means                                   | examples                                  |
 * |--------------|-----------------------------------------|-------------------------------------------|
 * | `neutral`    | a state; nothing to do because of it    | Scheduled, Closed, Waiting on customer    |
 * | `outline`    | an identifier, not a status             | #23747, a work-order status with its dot  |
 * | `primary`    | our move: we owe the next step          | Reply owed, Callback owed                 |
 * | `warning`    | needs a person now, not yet blocked     | customer ambiguous, unspammed             |
 * | `danger`     | blocked or a hard stop; overdue         | Overdue · contractor 2d, customer unknown |
 * | `success`    | done or clear                           | customer matched, Ledger clear            |
 * | `riley`      | who: Riley said or did this             | Hypothesis, Riley draft                   |
 * | `customer`, `contractor`, `internal` | who: the audience | the audience of a turn or pane       |
 *
 * If half the chips on a screen are amber, none of them signals: before choosing `warning`, ask whether a person
 * has to act on it today. Basis text (why the chip says what it says) goes in `InfoChip`, never only in `title=`,
 * because a phone cannot hover.
 *
 * Below the pane breakpoint (900px) a chip may wrap onto a second line instead of clipping its label.
 */
export const chipVariants = cva(
  [
    'inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 text-xs font-medium leading-none [&_svg]:size-3',
    // Phone: wrap rather than clip ("Double-bill risk: D…" at 390, #1232). max-w-full is what lets a long chip wrap.
    // max-pane is derived from --breakpoint-pane, the shared boundary for the single-pane phone shell.
    'max-pane:h-auto max-pane:min-h-5 max-pane:max-w-full max-pane:whitespace-normal max-pane:py-0.5 max-pane:leading-tight',
  ],
  {
    variants: {
      tone: {
        neutral: 'border-transparent bg-muted text-muted-foreground',
        outline: 'border-border-strong bg-card text-foreground',
        primary: 'border-transparent bg-primary-soft text-primary',
        success: 'border-transparent bg-success-soft text-success',
        warning: 'border-transparent bg-warning-soft text-warning',
        danger: 'border-destructive/30 bg-destructive-soft text-destructive',
        riley: 'border-riley-border bg-riley-soft text-riley',
        customer: 'border-transparent bg-audience-customer-soft text-audience-customer',
        contractor: 'border-transparent bg-audience-contractor-soft text-audience-contractor',
        internal: 'border-transparent bg-audience-internal-soft text-audience-internal',
      },
      size: { sm: 'h-[18px] px-1 text-2xs', md: '', lg: 'h-6 px-2 text-sm' },
    },
    defaultVariants: { tone: 'neutral', size: 'md' },
  },
)

export type ChipTone = NonNullable<VariantProps<typeof chipVariants>['tone']>

export function Chip({ className, tone, size, ...props }: React.ComponentProps<'span'> & VariantProps<typeof chipVariants>) {
  return <span data-slot="chip" className={cn(chipVariants({ tone, size }), className)} {...props} />
}

/** A Desk ticket number. Monospace digits so a column of them lines up. */
export function TicketChip({ number, relation, title }: { number: string | number; relation?: string; title?: string }) {
  return (
    <Chip tone="outline" title={title} className="tabular font-mono text-[11px]">
      #{number}
      {relation ? <span className="font-sans font-normal text-muted-foreground">{relation}</span> : null}
    </Chip>
  )
}

export type InfoChipProps = Omit<React.ComponentProps<'button'>, 'children'> &
  VariantProps<typeof chipVariants> & {
    /** The basis: why the chip says what it says. Shown on hover, and in a popover on click, tap or Enter. */
    info: React.ReactNode
    children: React.ReactNode
  }

/**
 * A chip that carries its basis (#1232, finding 15). `title=` and hover-only tooltips never reach a phone or a
 * keyboard, so the chip is a button: hover still shows the basis as a tooltip, and click, tap, Enter or Space open
 * it in a small popover that Escape closes. Clicks and those keys stop at the chip, so a chip inside a clickable row
 * does not also open the row.
 */
export function InfoChip({ info, tone, size, className, children, onClick, onKeyDown, ...props }: InfoChipProps) {
  return (
    <Popover>
      <Tooltip content={<span className="block max-w-80 whitespace-normal">{info}</span>}>
        <PopoverTrigger
          render={
            <button
              type="button"
              data-slot="chip"
              data-info-chip=""
              className={cn(
                chipVariants({ tone, size }),
                'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                className,
              )}
              {...props}
              onClick={(event) => {
                event.stopPropagation()
                onClick?.(event)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ' || event.key === 'Escape') event.stopPropagation()
                onKeyDown?.(event)
              }}
            />
          }
        >
          {children}
          <Info aria-hidden className="opacity-70" />
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent className="w-auto max-w-80 text-xs leading-snug" onClick={(event) => event.stopPropagation()}>
        {info}
      </PopoverContent>
    </Popover>
  )
}

/* ------------------------------------------------------------------------------------------ */

export type IdentityState = 'resolved' | 'ambiguous' | 'unresolved' | 'human_set' | 'remembered'

const IDENTITY: Record<IdentityState, { label: string; tone: ChipTone }> = {
  // Says what it is about: a bare "resolved" next to the customer name read as "this case is resolved".
  resolved: { label: 'customer matched', tone: 'success' },
  human_set: { label: 'customer set by hand', tone: 'success' },
  remembered: { label: 'linked from memory', tone: 'warning' },
  ambiguous: { label: 'customer ambiguous', tone: 'warning' },
  unresolved: { label: 'customer not identified', tone: 'danger' },
}

/**
 * The identity and ledger chips of the case header. The `desk` kind (with the "Desk:" prefix #790 asked to drop)
 * is gone (#1232); the case header's LaneStatusChip says the conversation state.
 *
 * @deprecated Only `case-header.tsx` still imports this. Inline the two chips there and delete this (#1232).
 */
export type StatePillProps = ({ kind: 'ledger'; open: number } | { kind: 'identity'; state: IdentityState }) & {
  size?: 'md' | 'lg'
}

/** @deprecated See StatePillProps. */
export function StatePill(props: StatePillProps) {
  if (props.kind === 'ledger') {
    return (
      <Chip tone={props.open > 0 ? 'warning' : 'success'} size={props.size ?? 'lg'}>
        <span className="font-normal opacity-75">Ledger:</span>{' '}
        {props.open > 0
          ? props.size === 'md'
            ? `${props.open} open`
            : `${props.open} open obligation${props.open === 1 ? '' : 's'}`
          : 'clear'}
      </Chip>
    )
  }
  const i = IDENTITY[props.state]
  return (
    <Chip tone={i.tone} size={props.size === 'lg' ? 'lg' : 'md'}>
      {i.label}
    </Chip>
  )
}

/** Due time. Red when overdue; the label is pre-formatted by the caller ("Today 2pm", "Overdue 1d"). */
export function DueChip({ label, overdue = false, title }: { label: string; overdue?: boolean; title?: string }) {
  return (
    <Chip tone={overdue ? 'danger' : 'neutral'} title={title} data-overdue={overdue || undefined}>
      {label}
    </Chip>
  )
}

/* ---------------------------------------- work orders ---------------------------------------- */

export type WorkOrderStatus = 'draft' | 'sent' | 'in_progress' | 'submitted' | 'returned' | 'approved' | 'expired' | 'revoked' | 'paid'

export const WO_STATUS: Record<WorkOrderStatus, { label: string; dot: string }> = {
  draft: { label: 'Draft', dot: 'bg-wo-draft' },
  sent: { label: 'Sent', dot: 'bg-wo-sent' },
  in_progress: { label: 'In progress', dot: 'bg-wo-in-progress' },
  submitted: { label: 'Submitted', dot: 'bg-wo-submitted' },
  returned: { label: 'Returned', dot: 'bg-wo-returned' },
  approved: { label: 'Approved', dot: 'bg-wo-approved' },
  expired: { label: 'Expired', dot: 'bg-wo-expired' },
  revoked: { label: 'Revoked', dot: 'bg-wo-revoked' },
  paid: { label: 'Paid', dot: 'bg-wo-paid' },
}

export function StatusDot({ className }: { className?: string }) {
  return <span aria-hidden className={cn('inline-block size-2 shrink-0 rounded-full', className)} />
}

export function WorkOrderStatusChip({ status }: { status: WorkOrderStatus }) {
  const s = WO_STATUS[status]
  return (
    <Chip tone="outline">
      <StatusDot className={s.dot} />
      {s.label}
    </Chip>
  )
}
